import { useCallback, useEffect, useRef, useState } from "react";

import { isUserRejection } from "../services/relay.service";
import { getMetaMaskSigner } from "../services/contract.service";
import {
  CollectedSignature,
  VerdictSpec,
  describeChainError,
  orderSignatures,
  recoverSigner,
} from "../services/challenge.service";

// Signature collection for a V10.1 panel determination — recordOutcome (deed / verification
// stage) and judgePreMint (pre-mint) share it; the VerdictSpec says which contract, which seats
// and how many signatures.
//
//   idle → loading      building the spec (reads the panel, threshold, nonce)
//        → collecting   gathering signatures: the connected wallet signs if it holds a seat, and
//                       signatures from the other seats are pasted in (they sign the same typed
//                       data from their own wallet — see the dialog copy)
//        → signing      one MetaMask typed-data prompt (no gas)
//        → submitting   the determination transaction, sender pays gas
//        → success | error
//
// The verdict is part of the digest, so the caller must build a NEW spec whenever the selected
// verdict changes — collected signatures are cleared with it. The digest also carries the
// per-challenge nonce, so a determination recorded by someone else between collecting and
// submitting invalidates every signature here; the contract reports that as
// InsufficientPanelSignatures and the user starts again.

export type PanelVerdictStatus = "idle" | "loading" | "collecting" | "signing" | "submitting" | "success" | "error";

export const usePanelVerdict = (build: (() => Promise<VerdictSpec>) | null, viewerAddress?: string | null) => {
  const [spec, setSpec] = useState<VerdictSpec | null>(null);
  const [signatures, setSignatures] = useState<CollectedSignature[]>([]);
  const [status, setStatus] = useState<PanelVerdictStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const safeSet = useCallback(<T>(setter: (v: T) => void, value: T) => {
    if (mounted.current) {
      setter(value);
    }
  }, []);

  // The builder the collected signatures belong to, so a revalidate that resolves after the
  // verdict changed does not write its result over the new one.
  const buildRef = useRef(build);
  buildRef.current = build;

  // Rebuild whenever the caller hands over a new builder (i.e. the verdict changed).
  useEffect(() => {
    setSignatures([]);
    setTxHash(null);
    setError(null);
    if (!build) {
      setSpec(null);
      setStatus("idle");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    build()
      .then((s) => {
        if (!cancelled) {
          setSpec(s);
          setStatus("collecting");
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setSpec(null);
          setError(describeChainError(e));
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [build]);

  const viewer = viewerAddress?.toLowerCase() ?? null;
  const seats = spec?.seats ?? [];
  const threshold = spec?.threshold ?? 0;
  const viewerIsSeat = !!viewer && seats.some((s) => s.toLowerCase() === viewer);
  const viewerHasSigned = !!viewer && signatures.some((s) => s.signer.toLowerCase() === viewer);
  const signedSeats = new Set(signatures.map((s) => s.signer.toLowerCase()));
  const canSubmit = !!spec && signatures.length >= threshold && (status === "collecting" || status === "error");

  // Sign with the connected wallet. Only offered when the wallet holds a seat — a signature from
  // anyone else counts for nothing on chain.
  const signWithWallet = useCallback(async (): Promise<CollectedSignature | null> => {
    if (!spec) {
      return null;
    }
    safeSet(setError, null);
    safeSet(setStatus, "signing");
    try {
      const signer = await getMetaMaskSigner();
      const address = (await signer.getAddress()).toLowerCase();
      if (!spec.seats.some((s) => s.toLowerCase() === address)) {
        throw new Error("The connected wallet does not hold a seat on this panel, so its signature would not count.");
      }
      const signature = await spec.sign(signer);
      const entry = { signer: address, signature };
      safeSet(setSignatures, [...signatures.filter((s) => s.signer.toLowerCase() !== address), entry]);
      safeSet(setStatus, "collecting");
      return entry;
    } catch (e: any) {
      if (isUserRejection(e)) {
        safeSet(setStatus, "collecting");
        return null;
      }
      safeSet(setError, describeChainError(e));
      safeSet(setStatus, "error");
      return null;
    }
  }, [spec, signatures, safeSet]);

  // A signature produced by another seat, out of band. It is attributed by recovering the signer
  // against the digest the contract holds right now, so a signature over a different verdict, a
  // stale nonce, or from a non-seat is rejected here rather than at submission.
  const addSignature = useCallback(
    async (signature: string): Promise<boolean> => {
      if (!spec) {
        return false;
      }
      safeSet(setError, null);
      const sig = signature.trim();
      try {
        const digest = await spec.digest();
        const signer = recoverSigner(digest, sig).toLowerCase();
        if (!spec.seats.some((s) => s.toLowerCase() === signer)) {
          throw new Error(
            "That signature does not belong to any seat on this panel for this exact verdict — check the verdict " +
              "matches and the signature is current."
          );
        }
        safeSet(setSignatures, [
          ...signatures.filter((s) => s.signer.toLowerCase() !== signer),
          { signer, signature: sig },
        ]);
        if (status === "error") {
          safeSet(setStatus, "collecting");
        }
        return true;
      } catch (e: any) {
        safeSet(setError, e?.message?.startsWith("That signature") ? e.message : "That is not a valid signature.");
        return false;
      }
    },
    [spec, signatures, status, safeSet]
  );

  const submit = useCallback(async (): Promise<string | null> => {
    if (!spec || signatures.length < spec.threshold) {
      return null;
    }
    safeSet(setError, null);
    safeSet(setStatus, "submitting");
    try {
      const hash = await spec.submit(orderSignatures(signatures));
      safeSet(setTxHash, hash);
      safeSet(setStatus, "success");
      return hash;
    } catch (e: any) {
      if (isUserRejection(e)) {
        safeSet(setStatus, "collecting");
        return null;
      }
      safeSet(setError, describeChainError(e));
      safeSet(setStatus, "error");
      return null;
    }
  }, [spec, signatures, safeSet]);

  // Re-read the spec (seats, nonce) WITHOUT discarding what was collected under it: a signature is
  // kept only if it still recovers to its recorded seat against the digest the contract holds now.
  // For a dialog reopened on the same verdict — a determination recorded in between bumps the
  // nonce, so every signature drops out here rather than as a revert at submission.
  const revalidate = useCallback(async () => {
    if (!build || status === "signing" || status === "submitting") {
      return;
    }
    const current = build;
    safeSet(setError, null);
    safeSet(setStatus, "loading");
    try {
      const fresh = await current();
      const digest = await fresh.digest();
      const seatSet = new Set(fresh.seats.map((s) => s.toLowerCase()));
      const kept = signatures.filter((c) => {
        try {
          const signer = recoverSigner(digest, c.signature).toLowerCase();
          return signer === c.signer.toLowerCase() && seatSet.has(signer);
        } catch {
          return false;
        }
      });
      if (buildRef.current !== current) {
        return;
      }
      safeSet(setSpec, fresh);
      safeSet(setSignatures, kept);
      safeSet(setStatus, "collecting");
    } catch (e: any) {
      if (buildRef.current !== current) {
        return;
      }
      safeSet(setSpec, null);
      safeSet(setSignatures, []);
      safeSet(setError, describeChainError(e));
      safeSet(setStatus, "error");
    }
  }, [build, signatures, status, safeSet]);

  const reset = useCallback(() => {
    setSignatures([]);
    setError(null);
    setTxHash(null);
    setStatus(spec ? "collecting" : "idle");
  }, [spec]);

  const isPending = status === "loading" || status === "signing" || status === "submitting";

  return {
    spec,
    viewer,
    seats,
    threshold,
    signatures,
    signedSeats,
    viewerIsSeat,
    viewerHasSigned,
    canSubmit,
    status,
    error,
    txHash,
    isPending,
    signWithWallet,
    addSignature,
    submit,
    revalidate,
    reset,
  };
};

export default usePanelVerdict;
